import { Grid, Typography, useEventCallback } from '@mui/material';
import { Cropper } from 'shell/components/shared/cropper';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik, useFormikContext } from 'formik';
import {
  CertificationFormVM,
  DocumentCivilUnionFormVM,
} from 'front-office/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import DescriptionForm from 'civil-status/components/shared/description-document';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import createSchema from './create-schema-civil-union';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { transformCertificateTitle } from 'front-office/utils/transform-certificate-title';
import UnionFormComponent from 'civil-status/components/shared/civil-union-form';
import { DocumentCivilUnionCM } from 'shell/models/document-civil-union';
import {
  transformDocumentCivilUnion,
  transformDocumentCivilUnionRequest,
} from 'civil-status/utils/transform-document-civil-union';
import AnnotationForm from 'civil-status/components/shared/annotation';
import DocumentVisualizationsButtonsForm from './buttons';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';

interface DocumentVisualizationMarriageProps {
  onBack: () => void;
  onNext: (values: DocumentCivilUnionFormVM) => void;
  type: string;
}

const DocumentVisualizationCivilUnionComponent = ({
  onBack,
  onNext,
  type,
}: DocumentVisualizationMarriageProps): React.ReactElement => {
  const [visibilityWarning, setVisibilityWarning] = useState<boolean>(false);

  const formContext = useFormikContext<CertificationFormVM>();

  const { values: params } = formContext;

  const [civilUnion] = useHttpFetch<HttpResponse<DocumentCivilUnionCM>, DocumentCivilUnionFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/visualizzazione-atto-unione-civile/${
      params.chosenDocument?.year
    }-${params.chosenDocument?.numReg}-${params.chosenDocument?.documentNumber}-${
      params.chosenDocument?.parte || 'null'
    }-${params.chosenDocument?.serie || 'null'}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentCivilUnion,
    }
  );

  const updateCivilUnion = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/modifica-atto-unione-civile`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const formikForm = useFormik<DocumentCivilUnionFormVM>({
    initialValues: civilUnion.data as DocumentCivilUnionFormVM,
    enableReinitialize: true,
    validationSchema: createSchema,
    onSubmit: (values: DocumentCivilUnionFormVM, context) => {
      if (mode === PageMode.Edit) {
        const data = transformDocumentCivilUnionRequest(values);
        (
          updateCivilUnion({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          context.setFieldValue('indicatorUpdate', '');
        });
        return;
      }

      (
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.chosenDocument?.documentNumber,
              numeroRegistro: params.chosenDocument?.numReg,
              parte: params.chosenDocument?.parte,
              serie1: `${params.chosenDocument?.serie || ''}${params.chosenDocument?.serie2 || ''}`,
              anno: params.chosenDocument?.year,
            },
          },
        }) as ResponsePromise<void>
      ).then(() => {
        onNext(values);
      });
    },
  });

  const onClickBack = useEventCallback(() => {
    if (!params.chosenDocument?.year) {
      onBack();
    }

    (
      unlockDocument({
        data: {
          key: {
            numeroAtto: params.chosenDocument?.documentNumber,
            numeroRegistro: params.chosenDocument?.numReg,
            parte: params.chosenDocument?.parte,
            serie1: `${params.chosenDocument?.serie || ''}${params.chosenDocument?.serie2 || ''}`,
            anno: params.chosenDocument?.year,
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      onBack();
    });
  });

  const { values, errors, touched, setFieldValue: onChange } = formikForm;

  useEffect(() => {
    if (values?.indicatorUpdate) {
      setVisibilityWarning(true);
    } else {
      setVisibilityWarning(false);
    }
  }, [values?.indicatorUpdate]);

  useEffect(() => {
    return () => {
      if (values) {
        if (params.chosenDocument?.year) {
          unlockDocument({
            data: {
              key: {
                numeroAtto: params.chosenDocument?.documentNumber,
                numeroRegistro: params.chosenDocument?.numReg,
                parte: params.chosenDocument?.parte,
                serie1: `${params.chosenDocument?.serie || ''}${
                  params.chosenDocument?.serie2 || ''
                }`,
                anno: params.chosenDocument?.year,
              },
            },
          });
        }
      }
    };
  }, [params?.chosenDocument?.documentNumber]);

  const atto = useMemo(() => {
    return transformCertificateTitle(formContext.values?.certificate);
  }, [formContext.values?.certificate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Visualizzazione Atto {atto.type}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={!formikForm.isValid ? `Sono presenti errori` : ''}
            messages={
              !formikForm.isValid
                ? [
                    {
                      id: 1,
                      value: "Entrare in modifica dell'atto e compilare i campi obbligatori",
                    },
                  ]
                : []
            }
            visibility={!formikForm.isValid}
            onChangeVisibility={noop}
          />
        </Grid>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Warning}
            title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
            messages={
              formikForm.values?.indicatorUpdate
                ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
                : []
            }
            visibility={visibilityWarning}
            onChangeVisibility={() => setVisibilityWarning(false)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DocumentVisualizationsButtonsForm
            values={values}
            type={type}
            handleSubmit={() => formikForm.handleSubmit()}
            mode={mode}
            onBack={onClickBack}
            onChange={onChange}
            setMode={setMode}
          />
          <DescriptionForm
            values={{
              registerNumber: params.chosenDocument?.numReg || '',
              year: params.chosenDocument?.year || '',
              documentNumber: params.chosenDocument?.documentNumber || '',
              portion: params.chosenDocument?.parte || '',
              serie: params.chosenDocument?.serie || '',
            }}
          />
          {mode === PageMode.Detail || mode === PageMode.Edit ? (
            <UnionFormComponent
              onChange={onChange}
              onChanges={onChanges}
              values={values}
              errors={errors}
              mode={mode}
              touched={touched}
            />
          ) : undefined}
          <AnnotationForm
            values={values}
            onChange={onChange}
            mode={mode}
            type={type}
            setMode={setMode}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <StickyDiv>
            <Cropper
              images={values?.uploadFile?.raw || []}
              disabled={!values?.uploadFile?.raw?.length}
              onCut={(blob, indexImage) => {
                if (!blob) {
                  return;
                }
                blobToBase64(blob).then((base64) => {
                  onChange('uploadFile', {
                    ...(values?.uploadFile || {}),
                    raw:
                      values?.uploadFile?.raw?.map((el, index) =>
                        index === indexImage ? base64 : el
                      ) || [],
                    rawTiff: base64,
                  });
                });
              }}
            />
          </StickyDiv>
        </Grid>
      </Grid>
    </>
  );
};

DocumentVisualizationCivilUnionComponent.displayName = 'Document Civil Union form component';

const DocumentVisualizationCivilUnionForm = memo(DocumentVisualizationCivilUnionComponent);

export default DocumentVisualizationCivilUnionForm;
